import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import '../fichierCss/Actualites.css';
import RessourceApi from '../api/RessourceApi';
import Ressource from '../modeles/Ressource';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ListeEvenements from '../composants/ListeEvenements';
import '../fichierCss/Accueil.css';
import { obtenirUrlImage } from '../utils/ressourceImageUtils';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
  container: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
  paper: {
    padding: theme.spacing(1),
    textAlign: 'justify',
    color: theme.palette.text.secondary,
    marginLeft:0,
    marginBottom:'1em',
  },
}));

interface EvenementsContainerProps {
  variant?: string;
  selectedIndex?: number;
  onListItemClick?: (event: React.MouseEvent, index: number) => void;
  onEvenementClick?: (evenement: Ressource) => void;
  onEvenementsChargees?: (premier: Ressource) => void;
}

const EvenementsContainer = memo(({
  variant = 'full',
  selectedIndex: selectedIndexControle,
  onListItemClick,
  onEvenementClick,
  onEvenementsChargees,
}: EvenementsContainerProps) => {
    const [evenements, setEvenements] = useState<Ressource[]>([]);
    const [selectedIndexInterne, setSelectedIndexInterne] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const classes = useStyles();

    const selectedIndex = typeof selectedIndexControle === 'number'
      ? selectedIndexControle
      : selectedIndexInterne;

    useEffect(() => {
      let isMounted = true;

      const fetchEvenements = async () => {
        setIsLoading(true);
        setHasError(false);

        try {
          const data = await RessourceApi.obtenirRessourcesParCategorie('Evenement');
          if (!isMounted) {
            return;
          }

          const resultats = Array.isArray(data) ? data : [];
          setEvenements(resultats);
          setSelectedIndexInterne(0);
          if (resultats.length > 0 && onEvenementsChargees) {
            onEvenementsChargees(resultats[0]);
          }
        } catch (error) {
          if (!isMounted) {
            return;
          }
          setEvenements([]);
          setHasError(true);
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      };

      fetchEvenements();

      return () => {
        isMounted = false;
      };
    }, []);

    const evenementActif = useMemo(() => {
      if (evenements.length === 0) {
        return null;
      }

      return evenements[selectedIndex] || evenements[0];
    }, [evenements, selectedIndex]);

    const imageEvenement = useMemo(() => {
      if (!evenementActif) return '';
      return obtenirUrlImage(evenementActif);
    }, [evenementActif]);

    const handleListItemClick = useCallback((event, index) => {
      setSelectedIndexInterne(index);
      if (onListItemClick) {
        onListItemClick(event, index);
      }
    }, [onListItemClick]);

    const handleSelectionDepuisCarte = useCallback((event, index) => {
      setSelectedIndexInterne(index);
      if (onListItemClick) {
        onListItemClick(event, index);
      }
    }, [onListItemClick]);

    const handleEvenementClick = useCallback((evenementClique: Ressource) => {
      const index = evenements.findIndex((item) => item.id === evenementClique.id);
      const prochainIndex = index >= 0 ? index : 0;
      setSelectedIndexInterne(prochainIndex);

      if (onEvenementClick) {
        onEvenementClick(evenementClique);
      }
    }, [evenements, onEvenementClick]);
        
       return (
          evenementActif != null ? (
            variant === 'list' ? (
              <ListeEvenements
                evenements={evenements}
                isLoading={isLoading}
                hasError={hasError}
                selectedIndex={selectedIndex}
                handleListItemClick={handleListItemClick}
                onEvenementClick={handleEvenementClick}
              />
            ) : (
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={8}>
                  <Box sx={{ borderRadius: 2, overflow: 'hidden', background: '#fff', border: '1px solid #e0e8f0' }}>
                    <Box sx={{ width: '100%', height: 320, overflow: 'hidden', bgcolor: '#e8f0f7' }}>
                      <img
                        src={imageEvenement}
                        alt={evenementActif?.titre}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: imageEvenement ? 'block' : 'none' }}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      />
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <Chip label="Evenement" size="small" sx={{ mb: 1.5, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5, color: '#1a2740', lineHeight: 1.3 }}>
                        {evenementActif?.titre || ''}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#b45309', lineHeight: 1.7 }}>
                        {evenementActif?.resume || evenementActif?.description || ''}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <ListeEvenements
                    evenements={evenements}
                    isLoading={isLoading}
                    hasError={hasError}
                    selectedIndex={selectedIndex}
                    handleListItemClick={handleListItemClick}
                    onEvenementClick={handleEvenementClick}
                  />
                </Grid>
              </Grid>
            )
          ) : (
            <div style={{ padding: '1rem' }}>{hasError ? 'Impossible de charger les evenements.' : 'Aucun evenement a afficher.'}</div>
          )
        );
});
export default  EvenementsContainer;